import * as R from 'ramda';
import 'react-image-crop/dist/ReactCrop.css';
import ReactCrop, { centerCrop, makeAspectCrop } from 'react-image-crop';
import React, { useRef, useMemo, useState, useEffect, useCallback } from 'react';
// components
import { FormFooter } from '../form-footer';
// helpers/constants
import * as G from '../../helpers';
// icons
import * as I from '../../svgs';
// ui
import { Box, Flex } from '../../ui';
//////////////////////////////////////////////////

const iconColor = G.getTheme('colors.dark.blue');

const useDebounceEffect = (fn, waitTime, deps = []) => {
  useEffect(() => {
    const t = setTimeout(() => fn(...deps), waitTime);

    return () => clearTimeout(t);
  }, deps);
};

const centerAspectCrop = (mediaWidth, mediaHeight, aspect) => centerCrop(
  makeAspectCrop({ unit: '%', width: 90 }, aspect, mediaWidth, mediaHeight),
  mediaWidth,
  mediaHeight,
);

const canvasPreview = (
  image,
  canvas,
  crop,
  scale = 1,
  rotate = 0,
) => {
  const TO_RADIANS = R.divide(Math.PI, 180);
  const ctx = canvas.getContext('2d');

  if (!ctx) {
    throw new Error('No 2d context');
  }

  ctx.clearRect(0, 0, canvas.width, canvas.height);

  const pixelRatio = window.devicePixelRatio;
  const scaleX = R.divide(image.naturalWidth, image.width);
  const scaleY = R.divide(image.naturalHeight, image.height);

  const cropWidth = Math.floor(crop.width * scaleX * pixelRatio);
  const cropHeight = Math.floor(crop.height * scaleY * pixelRatio);

  ctx.scale(pixelRatio, pixelRatio);
  ctx.imageSmoothingQuality = 'high';

  const cropX = R.multiply(crop.x, scaleX);
  const cropY = R.multiply(crop.y, scaleY);

  const rotateRads = R.multiply(rotate, TO_RADIANS);

  const rotatedWidth =
    Math.abs(cropWidth * Math.cos(rotateRads)) + Math.abs(cropHeight * Math.sin(rotateRads));
  const rotatedHeight =
    Math.abs(cropHeight * Math.cos(rotateRads)) + Math.abs(cropWidth * Math.sin(rotateRads));

  const centerX = R.divide(rotatedWidth, 2);
  const centerY = R.divide(rotatedHeight, 2);

  // eslint-disable-next-line no-param-reassign
  canvas.width = rotatedWidth;
  // eslint-disable-next-line no-param-reassign
  canvas.height = rotatedHeight;

  ctx.save();

  ctx.translate(centerX, centerY);
  ctx.rotate(rotateRads);

  ctx.drawImage(
    image,
    cropX,
    cropY,
    crop.width * scaleX,
    crop.height * scaleY,
    -cropWidth / 2,
    -cropHeight / 2,
    cropWidth,
    cropHeight,
  );

  ctx.restore();
};

const ImageCropper = (props) => {
  const { src = '', file, submitAction } = props;

  const [crop, setCrop] = useState();
  const [imgSrc, setImgSrc] = useState(src);
  const [rotate, setRotate] = useState(0);
  const [aspect, setAspect] = useState(16 / 9);
  const [completedCrop, setCompletedCrop] = useState();

  const imgRef = useRef(null);
  const previewCanvasRef = useRef(null);

  const handleImageLoad = (event) => {
    const { width, height } = event.currentTarget;

    setCrop(centerAspectCrop(width, height, aspect));
  };

  const handleRotate = (angle) => {
    setRotate((prevRotate) => (prevRotate + angle) % 360);
  };

  const generateFile = useCallback(async () => {
    const { current: image } = imgRef;

    const scaleX = R.divide(image.naturalWidth, image.width);
    const scaleY = R.divide(image.naturalHeight, image.height);

    const cropWidth = R.multiply(completedCrop.width, scaleX);
    const cropHeight = R.multiply(completedCrop.height, scaleY);

    const rotateRads = R.multiply(rotate, Math.PI / 180);

    const canvasWidth =
      Math.abs(cropWidth * Math.cos(rotateRads)) + Math.abs(cropHeight * Math.sin(rotateRads));
    const canvasHeight =
      Math.abs(cropHeight * Math.cos(rotateRads)) + Math.abs(cropWidth * Math.sin(rotateRads));

    // eslint-disable-next-line no-undef
    const offscreen = new OffscreenCanvas(canvasWidth, canvasHeight);

    const ctx = offscreen.getContext('2d');

    if (R.not(ctx)) {
      throw new Error('No 2d context');
    }

    const centerX = R.divide(canvasWidth, 2);
    const centerY = R.divide(canvasHeight, 2);

    ctx.save();
    ctx.translate(centerX, centerY);
    ctx.rotate(rotateRads);

    ctx.drawImage(
      image,
      R.multiply(completedCrop.x, scaleX),
      R.multiply(completedCrop.y, scaleY),
      cropWidth,
      cropHeight,
      -cropWidth / 2,
      -cropHeight / 2,
      cropWidth,
      cropHeight,
    );
    ctx.restore();

    const blob = await offscreen.convertToBlob({
      type: 'image/png',
    });

    const fileName = `${G.generateGuid()}.png`;
    const img = new File([blob], fileName, blob); // eslint-disable-line

    submitAction(img);
  }, [completedCrop, rotate]);

  useEffect(() => {
    if (R.or(G.isNilOrEmpty(file), G.isNotNilAndNotEmpty(src))) return;

    const reader = new FileReader(); // eslint-disable-line

    reader.addEventListener('load', () => setImgSrc(reader.result.toString()));
    reader.readAsDataURL(file);
  }, [src, file]);

  useDebounceEffect(
    async () => {
      if (G.isAllTrue(
        R.isNotNil(R.prop('current', imgRef)),
        R.isNotNil(R.prop('width', completedCrop)),
        R.isNotNil(R.prop('height', completedCrop)),
        R.isNotNil(R.prop('current', previewCanvasRef)),
      )) {
        canvasPreview(imgRef.current, previewCanvasRef.current, completedCrop, 1, rotate);
      }
    },
    100,
    [completedCrop, rotate],
  );

  useDebounceEffect(() => setAspect(undefined), 100, []);

  const { imageStyles, canvasStyles } = useMemo(() => ({
    imageStyles: {
      maxWidth: 1000, maxHeight: 500,
    },
    canvasStyles: {
      width: 'auto',
      maxWidth: 150,
      height: 'auto',
      maxHeight: 150,
      borderRadius: 4,
    },
  }), []);

  return (
    <Box minWidth={400}>
      <Flex>
        <ReactCrop
          crop={crop}
          minWidth={100}
          aspect={aspect}
          minHeight={100}
          onComplete={(crop) => setCompletedCrop(crop)}
          onChange={(_, percentCrop) => setCrop(percentCrop)}
        >
          <img
            alt='crop'
            src={imgSrc}
            ref={imgRef}
            style={imageStyles}
            onLoad={handleImageLoad}
          />
        </ReactCrop>
        <Flex ml={25} gap={20} flexDirection='column' justifyContent='flex-start'>
          <Flex gap={20}>
            <Box
              cursor='pointer'
              onClick={() => handleRotate(-90)}
            >
              {I.rotateLeft(iconColor, 20, 20)}
            </Box>
            <Box
              cursor='pointer'
              onClick={() => handleRotate(90)}
            >
              {I.rotateRight(iconColor, 20, 20)}
            </Box>
          </Flex>
          <Flex
            width={150}
            height={150}
            border='0.5px dashed'
            borderColor={iconColor}
            justifyContent='center'
          >
            {
              completedCrop &&
              <canvas style={canvasStyles} ref={previewCanvasRef} />
            }
          </Flex>
        </Flex>
      </Flex>
      <FormFooter
        boxStyles={{ mt: 25 }}
        submitAction={() => generateFile()}
        submitBtnText={G.getWindowLocale('actions:save', 'Save')}
      />
    </Box>
  );
};

export default ImageCropper;
